// Э-4. Форма заявки: слева поля ФТ-2.1, справа живая проверка окна и три варианта времени (ФТ-2.2).
// Выбор варианта подставляет время и РБУ. Сохранение — Ctrl+Enter.

import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../shared/api";
import { fmtDate, fmtTime, localISODate, zonedISO } from "../../shared/format";
import { Button, Drawer, Field, Input, Select, useToast } from "../../shared/ui";
import { api, can, CHANNELS, POUR, useCtx } from "../ctx";

export type Option = {
  kind: string; plant_id: string; plant_name: string; unit_id: string; unit_name: string; arrival_start: string;
  shift_minutes: number; interval_minutes: number; travel_minutes: number; trips_count: number; finish: string;
  free_from?: string | null; free_to?: string | null;
};
export type Availability = { free: boolean; reason: string | null; requested: Option | null; options: Option[] };
type Customer = { id: string; name: string; inn: string | null; phones: string | null; contacts: { id: string; full_name: string; phone: string; position: string }[] };
type Site = { id: string; name: string; intake_m3h: number | null; pour_method: string; travel: { plant_id: string; minutes: number }[] };

const KIND_LABEL: Record<string, string> = { earlier: "Раньше", later: "Позже", other_unit: "Точно ко времени", requested: "Точно ко времени", free: "Свободное окно" };

export function OrderForm({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string) => void }) {
  const { me, refs } = useCtx();
  const toast = useToast();
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [f, setF] = useState({
    // П-6 (22.10.2026): дата отгрузки пустая и обязательная — чтобы не сохранить заявку «на сегодня» по ошибке
    site_id: "", grade_id: "", volume_m3: "", day: "", time: "14:00", pour_method: "pump",
    intensity_m3h: "", channel: me.role === "dispatcher" ? "dispatch_phone" : "manager_phone", contact_id: "",
    plant_id: "", comment_driver: "", comment_operator: "", comment_client: "", confirm: me.role === "dispatcher",
  });
  // ФТ-2.4: регулярная заявка — шаблон «по дням недели с … по …» сам создаёт заявки на каждый день
  const [rec, setRec] = useState({ on: false, weekdays: [0, 1, 2, 3, 4, 5, 6] as number[]  /* П-5: завод работает без выходных */, date_to: "" });
  const [avail, setAvail] = useState<Availability | null>(null);
  const [checking, setChecking] = useState(false);
  const [choice, setChoice] = useState<Option | null>(null);
  const [dups, setDups] = useState<{ id: string; number: number }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: any) => setF((x) => ({ ...x, [k]: v }));
  const searchRef = useRef<HTMLInputElement>(null);

  // поиск клиента по названию, ИНН, телефону
  useEffect(() => {
    if (customer && q === customer.name) return;
    const t = setTimeout(async () => {
      const r = await api.get("/customers", { q: q || undefined, limit: 12 });
      setCustomers(r.items);
    }, 200);
    return () => clearTimeout(t);
  }, [q, customer]);

  const pickCustomer = async (c: Customer) => {
    setCustomer(c);
    setQ(c.name);
    setCustomers([]);
    const s = await api.get("/sites", { customer_id: c.id, limit: 200 });
    setSites(s.items);
    // ФТ-1.4: подставить последний объект, марку и контакт
    const last = await api.get("/orders", { customer: c.id, limit: 1000 });
    const prev = last[last.length - 1];
    const full = prev ? await api.get(`/orders/${prev.id}`) : null;
    setF((x) => ({
      ...x,
      site_id: full?.site.id || s.items[0]?.id || "",
      grade_id: full?.grade.id || x.grade_id,
      contact_id: full?.contact?.id || c.contacts[0]?.id || "",
    }));
  };

  const site = sites.find((s) => s.id === f.site_id);
  useEffect(() => {
    if (site) setF((x) => ({ ...x, pour_method: site.pour_method || x.pour_method, intensity_m3h: site.intake_m3h ? String(site.intake_m3h) : x.intensity_m3h }));
  }, [site?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const desired = useMemo(() => (f.day && /^\d{2}:\d{2}$/.test(f.time) ? zonedISO(f.day, f.time) : null), [f.day, f.time]);

  // живая проверка окна при каждом изменении
  useEffect(() => {
    setChoice(null);
    const vol = Number(f.volume_m3);
    if (!f.site_id || !vol || !desired) {
      setAvail(null);
      return;
    }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/scheduling/availability", {
          site_id: f.site_id, volume_m3: vol, desired_start: desired,
          intensity_m3h: f.intensity_m3h ? Number(f.intensity_m3h) : null, plant_id: f.plant_id || null,
        });
        setAvail(r);
        if (customer) {
          const d = await api.get("/orders/duplicates/check", { customer_id: customer.id, site_id: f.site_id, day: f.day, volume_m3: vol });
          setDups(d);
        }
      } catch (e) {
        setAvail(null);
        setError((e as Error).message);
      } finally {
        setChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [f.site_id, f.volume_m3, desired, f.intensity_m3h, f.plant_id, customer, f.day]);

  const saveRecurring = async () => {
    if (!customer || !f.site_id || !f.grade_id || !Number(f.volume_m3) || !f.day || !/^\d{2}:\d{2}$/.test(f.time)) {
      setError(!f.day ? "Укажите дату отгрузки" : "Заполните клиента, объект, марку, объём и время");
      return;
    }
    if (!rec.date_to || rec.date_to < f.day) { setError("Укажите, по какую дату повторять (не раньше даты начала)"); return; }
    if (!rec.weekdays.length) { setError("Отметьте хотя бы один день недели"); return; }
    setSaving(true);
    setError(null);
    try {
      const r = await api.post("/recurring-orders", {
        customer_id: customer.id, site_id: f.site_id, grade_id: f.grade_id, contact_id: f.contact_id || null,
        volume_m3: Number(f.volume_m3), start_time: f.time, weekdays: rec.weekdays, date_from: f.day, date_to: rec.date_to,
        intensity_m3h: f.intensity_m3h ? Number(f.intensity_m3h) : null, pour_method: f.pour_method, plant_id: f.plant_id || null,
        comment: f.comment_operator || null,
      });
      const g = r.generated || {};
      const extra = g.unplaced ? ` Не нашлось окна: ${g.unplaced} дн. — проверьте их в графике.` : g.shifted ? ` Сдвинуто по времени: ${g.shifted}.` : "";
      toast.ok(`Регулярная заявка сохранена: создано заявок ${g.created ?? 0}.${extra}`);
      onSaved("");
    } catch (e) { setError((e as Error).message); } finally { setSaving(false); }
  };

  const save = async (ignoreDuplicate = false) => {
    if (rec.on) return saveRecurring();
    if (!customer || !f.site_id || !f.grade_id || !Number(f.volume_m3) || !desired) {
      setError(!f.day ? "Укажите дату отгрузки" : "Заполните клиента, объект, марку, объём, дату и время");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        customer_id: customer.id, site_id: f.site_id, grade_id: f.grade_id, volume_m3: Number(f.volume_m3),
        desired_start: choice ? choice.arrival_start : desired, channel: f.channel, pour_method: f.pour_method,
        intensity_m3h: f.intensity_m3h ? Number(f.intensity_m3h) : null, contact_id: f.contact_id || null,
        plant_id: f.plant_id || null, comment_driver: f.comment_driver || null, comment_operator: f.comment_operator || null,
        comment_client: f.comment_client || null, confirm: f.confirm && can.dispatch(me.role), ignore_duplicate: ignoreDuplicate,
      };
      if (choice) {
        body.unit_id = choice.unit_id;
        body.arrival = choice.arrival_start;
      }
      const o = await api.post("/orders", body);
      toast.ok(`Заявка №${o.number} сохранена`);
      onSaved(o.id);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.details.duplicates) {
        if (confirm(`Похоже на дубль заявки №${e.details.duplicates.map((d: any) => d.number).join(", ")}. Всё равно сохранить?`)) {
          setSaving(false);
          return save(true);
        }
      } else if (e instanceof ApiError && e.status === 409 && e.details.availability) {
        setAvail(e.details.availability);
        setError("Окно занято — выберите один из вариантов справа");
      } else setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  });

  const options = avail ? [...avail.options] : [];
  return (
    <Drawer title="Новая заявка" onClose={onClose}
      footer={<>
        <Button variant="primary" onClick={() => save()} disabled={saving}>{rec.on ? "Сохранить регулярную · Ctrl+Enter" : "Сохранить · Ctrl+Enter"}</Button>
        <Button onClick={onClose}>Отмена</Button>
        {can.dispatch(me.role) && !rec.on && (
          <label className="check"><input type="checkbox" checked={f.confirm} onChange={(e) => set("confirm", e.target.checked)} />Сразу подтвердить и поставить в график</label>
        )}
        {error && <span className="err" role="alert" style={{ color: "var(--st-conflict)", alignSelf: "center" }}>{error}</span>}
      </>}>
      <div className="cols-2" style={{ gridTemplateColumns: "minmax(0,1.1fr) minmax(0,1fr)", alignItems: "start" }}>
        <div className="form-grid">
          <Field label="Клиент" wide hint="Поиск по названию, ИНН или телефону">
            <div style={{ position: "relative" }}>
              <Input ref={searchRef} autoFocus value={q} onChange={(e) => { setQ(e.target.value); setCustomer(null); }} placeholder="Начните вводить" />
              {!customer && customers.length > 0 && (
                <div className="panel" style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, maxHeight: 260, overflow: "auto" }} role="listbox">
                  {customers.map((c) => (
                    <div key={c.id} className="order-row" role="option" aria-selected={false} onClick={() => pickCustomer(c)}>
                      <b>{c.name}</b>
                      <span className="small muted">{[c.inn && `ИНН ${c.inn}`, c.phones].filter(Boolean).join(", ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Field>
          <Field label="Объект" wide>
            <Select value={f.site_id} onChange={(e) => set("site_id", e.target.value)} disabled={!customer} placeholder={customer ? "Выберите объект" : "Сначала клиент"}
              options={sites.map((s) => ({ value: s.id, label: s.name }))} />
          </Field>
          <Field label="Марка">
            <Select value={f.grade_id} onChange={(e) => set("grade_id", e.target.value)} placeholder="Марка бетона"
              options={refs.grades.map((g) => ({ value: g.id, label: g.code + (g.requires_lab_approval ? " (лаборатория)" : "") }))} />
          </Field>
          <Field label="Объём, м³">
            <Input inputMode="decimal" value={f.volume_m3} onChange={(e) => set("volume_m3", e.target.value.replace(",", "."))} />
          </Field>
          <Field label="Дата отгрузки *" hint={`Заявка принята ${fmtDate(localISODate())}`}>
            <Input type="date" required aria-required="true" value={f.day} min={localISODate()} onChange={(e) => set("day", e.target.value)}
              style={!f.day ? { borderColor: "var(--st-loading)" } : undefined} />
          </Field>
          <Field label="Время первой машины">
            <Input type="time" step={300} value={f.time} onChange={(e) => set("time", e.target.value)} />
          </Field>
          <Field label="Подача">
            <Select value={f.pour_method} onChange={(e) => set("pour_method", e.target.value)} options={Object.entries(POUR).map(([value, label]) => ({ value, label }))} />
          </Field>
          <Field label="Интенсивность приёма, м³/ч">
            <Input inputMode="decimal" value={f.intensity_m3h} onChange={(e) => set("intensity_m3h", e.target.value)} />
          </Field>
          <Field label="Канал">
            <Select value={f.channel} onChange={(e) => set("channel", e.target.value)}
              options={Object.entries(CHANNELS).filter(([k]) => !["recurring", "one_c"].includes(k)).map(([value, label]) => ({ value, label }))} />
          </Field>
          <Field label="Контакт на объекте">
            <Select value={f.contact_id} onChange={(e) => set("contact_id", e.target.value)} placeholder="—"
              options={(customer?.contacts || []).map((c) => ({ value: c.id, label: `${c.full_name}${c.position ? ", " + c.position : ""}` }))} />
          </Field>
          <Field label="Завод" hint="Пусто — любой, ближайший по времени в пути">
            <Select value={f.plant_id} onChange={(e) => set("plant_id", e.target.value)} placeholder="Любой" options={refs.plants.map((p) => ({ value: p.id, label: p.name }))} />
          </Field>
          <Field label="Комментарий водителю">
            <Input value={f.comment_driver} onChange={(e) => set("comment_driver", e.target.value)} />
          </Field>
          <Field label="Комментарий оператору" wide>
            <Input value={f.comment_operator} onChange={(e) => set("comment_operator", e.target.value)} placeholder="например: противоморозная добавка" />
          </Field>
          <div className="wide stack tight">
            <label className="check"><input type="checkbox" checked={rec.on} onChange={(e) => setRec({ ...rec, on: e.target.checked })} />
              Регулярная заявка: повторять по дням недели</label>
            {rec.on && (
              <div className="row tight">
                {["пн", "вт", "ср", "чт", "пт", "сб", "вс"].map((w, i) => (
                  <label key={w} className="check"><input type="checkbox" checked={rec.weekdays.includes(i)}
                    onChange={(e) => setRec({ ...rec, weekdays: e.target.checked ? [...rec.weekdays, i].sort() : rec.weekdays.filter((x) => x !== i) })} />{w}</label>
                ))}
                <span className="small">с {f.day ? f.day.split("-").reverse().join(".") : "—"} по</span>
                <Input type="date" aria-label="Повторять по" style={{ width: 160 }} value={rec.date_to} onChange={(e) => setRec({ ...rec, date_to: e.target.value })} />
              </div>
            )}
            {rec.on && <span className="small muted">Заявки создаются сами на каждый отмеченный день (на 14 дней вперёд, дальше — по мере приближения). Проверка окна справа — для первого дня.</span>}
          </div>
        </div>
        <div className="stack">
          <h3>Проверка окна</h3>
          {!avail && !checking && <p className="muted">Укажите объект, объём, дату и время — система сразу покажет, свободно ли.</p>}
          {checking && <div className="spinner" aria-label="Проверяю окно" />}
          {avail && (
            <>
              {avail.free && avail.requested ? (
                <div className="alert ok">
                  <div className="t">{fmtTime(avail.requested.arrival_start)} свободно: {avail.requested.plant_name}, {avail.requested.unit_name}</div>
                  <div className="d">{avail.requested.trips_count} рейсов, интервал {avail.requested.interval_minutes} мин, окончание ~{fmtTime(avail.requested.finish)}</div>
                </div>
              ) : (
                <div className="alert"><div className="t">{fmtTime(desired)} занято{f.plant_id ? " на выбранном заводе" : ""}</div>
                  <div className="d">{avail.reason || "Выберите ближайший вариант"}</div></div>
              )}
              <OptionList options={options} choice={choice} onPick={setChoice} />
              <div className="muted small">{dups.length ? `Похожая заявка: №${dups.map((d) => d.number).join(", ")}` : "Дублей нет"} · проверка при каждом изменении</div>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
}

/** Варианты при занятом окне: ближайшие (раньше, позже, другая РБУ) и свободные окна за сутки (П-7, П-8). */
export function OptionList({ options, choice, onPick }: { options: Option[]; choice: Option | null; onPick: (o: Option) => void }) {
  if (options.length === 0) return null;
  const near = options.filter((o) => o.kind !== "free");
  const free = options.filter((o) => o.kind === "free");
  const item = (o: Option) => {
    const active = choice?.unit_id === o.unit_id && choice?.arrival_start === o.arrival_start;
    const delta = o.shift_minutes === 0 ? `точно ко времени, ${o.travel_minutes} мин в пути`
      : `на ${fmtShift(Math.abs(o.shift_minutes))} ${o.shift_minutes < 0 ? "раньше" : "позже"}`;
    return (
      <label key={o.kind + o.unit_id + o.arrival_start} className="alert info opt" style={{ borderWidth: active ? 2.5 : 1.5, background: active ? "var(--accent-soft)" : "var(--panel)" }}>
        <input type="radio" name="opt" checked={active} onChange={() => onPick(o)} />
        <span className="grow">
          <b>{fmtTime(o.arrival_start)}, {o.plant_name}, {o.unit_name}</b>
          <span className="d">
            {o.kind === "free" && o.free_from && o.free_to ? `Свободно с ${fmtTime(o.free_from)} до ${fmtTime(o.free_to)}; ${delta}` : `${KIND_LABEL[o.kind]}: ${delta}`}; {o.trips_count} рейсов, интервал {o.interval_minutes} мин
          </span>
        </span>
      </label>
    );
  };
  return (
    <div className="stack tight" role="radiogroup">
      {near.length > 0 && <div className="muted small">Ближайшие варианты</div>}
      {near.map(item)}
      {free.length > 0 && <div className="muted small">Другие свободные окна за сутки</div>}
      {free.map(item)}
    </div>
  );
}

function fmtShift(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return [h ? `${h} ч` : "", m ? `${m} мин` : ""].filter(Boolean).join(" ");
}
